import React, { useEffect, useState, useMemo } from "react";
import {
  FiSearch,
  FiEye,
  FiClock,
  FiCheckCircle,
  FiTrash2,
  FiLock,
  FiUnlock,
  FiFilm,
} from "react-icons/fi";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import { toast } from "react-toastify";
import "./BancoBonos.css";
import { createPortal } from "react-dom";
import PageTitle from "../components/PageTitle";

const INVENTARIO_API = process.env.REACT_APP_API_INVENTARIO;
const API = process.env.REACT_APP_API_URL;

function BancoBonos() {

  const [assets, setAssets] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetFileKey, setResetFileKey] = useState(Date.now());
  const [soloProximos, setSoloProximos] = useState(false);
  const [showEntregaModal, setShowEntregaModal] = useState(false);
  const [codigoEntrega, setCodigoEntrega] = useState("");
  const [clienteEntrega, setClienteEntrega] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [referenciaEntrega, setReferenciaEntrega] = useState("");
  const [showUpload, setShowUpload] = useState(false);

  const [form, setForm] = useState({
    inventoryItem: "",
    fechaVencimiento: "",
    lote: "",
  });

  const [archivos, setArchivos] = useState([]);

  const [filtro, setFiltro] = useState({
    codigo: "",
    proveedor: "",
    producto: "",
    estado: "",
    lote: "",
    vigencia: "",
    search: "",
  });

  const [page, setPage] = useState(1);

  const limit = 20;

  // =========================
  // LOAD DATA
  // =========================

  const cargar = async () => {

    try {

      const token = localStorage.getItem("token");

      const [a, i] = await Promise.all([
        axios.get(`${API}/api/digital-assets`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(
          `${INVENTARIO_API}/items?limit=500`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      ]);

      setAssets(a.data || []);
      setInventario(i.data?.data || []);

    } catch (err) {

      console.error(err);

      toast.error("Error cargando banco de bonos");
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  // =========================
  // HANDLE INPUT
  // =========================

  const handleChange = (e) => {

    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // =========================
  // SUBIR BONOS
  // =========================

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!archivos.length) {
      toast.warning("Selecciona PDFs");
      return;
    }

    try {

      setLoading(true);

      for (const archivo of archivos) {

        const data = new FormData();

        Object.keys(form).forEach((key) => {
          data.append(key, form[key]);
        });

        data.append("archivo", archivo);

        const token = localStorage.getItem("token");

        await axios.post(
          `${API}/api/digital-assets`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      setForm({
        inventoryItem: "",
        fechaVencimiento: "",
        lote: "",
      });

      setArchivos([]);

      // limpia visual input file
      setResetFileKey(Date.now());

      await cargar();

      toast.success("Bonos cargados correctamente");

    } catch (err) {

      console.error(err);

      toast.error(
        err.response?.data?.message ||
        "Error subiendo bonos"
      );

    } finally {

      setLoading(false);
    }
  };

  // =========================
  // FILTROS
  // =========================

  const assetsFiltrados = useMemo(() => {

    const hoy = new Date();

    const limite = new Date();

    limite.setMonth(hoy.getMonth() + 2);

    return assets.filter((a) => {

      const proveedor =
        a.proveedor ||
        a.inventoryItem?.proveedor ||
        a.inventoryItem?.provider ||
        "";

      const producto =
        a.inventoryItem?.name || "";

      const matchCodigo =
        !filtro.codigo ||
        a.codigo === filtro.codigo;

      const matchProveedor =
        !filtro.proveedor ||
        proveedor === filtro.proveedor;

      const matchProducto =
        !filtro.producto ||
        producto === filtro.producto;

      const matchEstado =
        !filtro.estado ||
        a.estado === filtro.estado;

      const matchLote =
        !filtro.lote ||
        a.lote === filtro.lote;

      const matchVigencia = (() => {

        if (!filtro.vigencia) return true;

        if (!a.fechaVencimiento) return false;

        const hoy = new Date();

        const vencimiento = new Date(
          a.fechaVencimiento
        );

        const dias =
          Math.ceil(
            (vencimiento - hoy) /
            (1000 * 60 * 60 * 24)
          );

        switch (filtro.vigencia) {

          case "vencido":
            return dias < 0;

          case "30":
            return dias >= 0 && dias <= 30;

          case "60":
            return dias > 30 && dias <= 60;

          case "90":
            return dias > 60 && dias <= 90;

          case "90plus":
            return dias > 90;

          default:
            return true;
        }
      })();

      const textoBusqueda = `
        ${a.codigo || ""}
        ${a.proveedor || ""}
        ${a.inventoryItem?.proveedor || ""}
        ${a.inventoryItem?.provider || ""}
        ${a.inventoryItem?.name || ""}
        ${a.archivoNombre || ""}
        ${a.lote || ""}
        ${a.estado || ""}
        ${a.cliente || ""}
        ${a.wooOrderNumber || ""}
        ${a.wooOrderId || ""}
        ${a.despachoId?.codigo || ""}
      `
        .toLowerCase();

      const matchSearch =
        !filtro.search ||
        textoBusqueda.includes(
          filtro.search.toLowerCase()
        );

      const matchProximos =
        !soloProximos ||
        (() => {

          if (
            a.estado === "entregado" ||
            !a.fechaVencimiento
          ) {
            return false;
          }

          const vencimiento =
            new Date(a.fechaVencimiento);

          return (
            vencimiento >= hoy &&
            vencimiento <= limite
          );

        })();

      return (
        matchCodigo &&
        matchProveedor &&
        matchProducto &&
        matchEstado &&
        matchLote &&
        matchVigencia &&
        matchSearch &&
        matchProximos
      );
    });

  }, [assets, filtro, soloProximos]);

  // =========================
  // PAGINACIÓN
  // =========================

  const pages = Math.ceil(assetsFiltrados.length / limit);

  const paginatedAssets = useMemo(() => {

    const start = (page - 1) * limit;

    return assetsFiltrados.slice(
      start,
      start + limit
    );

  }, [assetsFiltrados, page]);

  // =========================
  // OPTIONS DINÁMICAS
  // =========================

  const sortAlphabetically = (items) =>
    [...new Set(items.filter(Boolean))]
      .sort((a, b) =>
        String(a).localeCompare(
          String(b),
          "es",
          {
            sensitivity: "base",
            numeric: true
          }
        )
      );

  const codigosOptions = sortAlphabetically(
    assets.map((a) => a.codigo)
  );

  const proveedoresOptions = sortAlphabetically(
    assets.map(
      (a) =>
        a.proveedor ||
        a.inventoryItem?.proveedor ||
        a.inventoryItem?.provider
    )
  );

  const productosOptions = sortAlphabetically(
    assets.map(
      (a) => a.inventoryItem?.name
    )
  );

  const lotesOptions = sortAlphabetically(
    assets.map((a) => a.lote)
  );

  const productosDigitales = inventario.filter(
    item => item.type === "Digital"
  );

  // ==========================================
  // RESUMEN GENERAL
  // ==========================================

  const resumenBonos = useMemo(() => {

    return {

      disponibles: assets.filter(
        a => a.estado === "disponible"
      ).length,

      reservados: assets.filter(
        a => a.estado === "reservado"
      ).length,

      entregados: assets.filter(
        a => a.estado === "entregado"
      ).length,

      proximos: assets.filter(a => {

        if (
          a.estado === "entregado" ||
          !a.fechaVencimiento
        ) {
          return false;
        }

        const hoy = new Date();

        const limite = new Date();

        limite.setMonth(
          hoy.getMonth() + 2
        );

        const fecha =
          new Date(a.fechaVencimiento);

        return (
          fecha >= hoy &&
          fecha <= limite
        );

      }).length

    };

  }, [assets]);

  // ==========================================
  // INVENTARIO ACTUAL POR PRODUCTO
  // ==========================================

  const inventarioPorProducto = useMemo(() => {

    return productosDigitales
      .map(producto => {

        const cantidad =
          assets.filter(
            a =>
              a.estado === "disponible" &&
              a.inventoryItem?._id === producto._id
          ).length;

        return {
          nombre: producto.name,
          cantidad
        };

      })

      .sort(
        (a, b) => a.cantidad - b.cantidad
      );

  }, [productosDigitales, assets]);

  // ==========================================
  // DISPONIBLES VS PRÓXIMOS A VENCER
  // ==========================================

  const disponiblesVsVencer = useMemo(() => {

    const hoy = new Date();

    const limite = new Date();

    limite.setMonth(
      hoy.getMonth() + 2
    );

    const proximos = assets.filter(a => {

      if (
        a.estado !== "disponible" ||
        !a.fechaVencimiento
      ) {
        return false;
      }

      const fecha =
        new Date(a.fechaVencimiento);

      return (
        fecha >= hoy &&
        fecha <= limite
      );

    }).length;

    const disponibles =
      assets.filter(
        a => a.estado === "disponible"
      ).length;

    return [
      {
        name: "Disponibles",
        value: disponibles
      },
      {
        name: "Próx. vencer",
        value: proximos
      }
    ];

  }, [assets]);

  // ==========================================
  // PRODUCTOS PRÓXIMOS A VENCER
  // ==========================================

  const proximosPorProducto = useMemo(() => {

    const hoy = new Date();

    const limite = new Date();

    limite.setMonth(
      hoy.getMonth() + 2
    );

    const mapa = {};

    assets.forEach(a => {

      if (
        a.estado !== "disponible" ||
        !a.fechaVencimiento
      ) {
        return;
      }

      const fecha =
        new Date(a.fechaVencimiento);

      if (
        fecha < hoy ||
        fecha > limite
      ) {
        return;
      }

      const nombre =
        a.inventoryItem?.name ||
        "Sin producto";

      mapa[nombre] =
        (mapa[nombre] || 0) + 1;

    });

    return Object.entries(mapa)
      .map(([nombre, cantidad]) => ({
        nombre,
        cantidad
      }))
      .sort(
        (a, b) => b.cantidad - a.cantidad
      );

  }, [assets]);

  const toTitleCase = (text = "") => {

    const minusculas = [
      "de",
      "del",
      "la",
      "las",
      "el",
      "los",
      "y",
      "e",
      "o",
      "u",
      "en",
      "a",
      "al",
      "por",
      "para",
      "con"
    ];

    return text
      .toLowerCase()
      .split(" ")
      .map((word, index) => {

        if (
          index > 0 &&
          minusculas.includes(word)
        ) {
          return word;
        }

        return (
          word.charAt(0).toUpperCase() +
          word.slice(1)
        );

      })
      .join(" ");
  };

  const calcularEstadoVencimiento = (fechaVencimiento) => {
    if (!fechaVencimiento) {
      return null;
    }

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const [anio, mes, dia] = fechaVencimiento
      .split("-")
      .map(Number);

    const vencimiento = new Date(anio, mes - 1, dia);
    vencimiento.setHours(0, 0, 0, 0);

    const diferenciaMilisegundos =
      vencimiento.getTime() - hoy.getTime();

    const diasRestantes = Math.ceil(
      diferenciaMilisegundos / (1000 * 60 * 60 * 24)
    );

    if (diasRestantes < 0) {
      return {
        tipo: "vencido",
        mensaje: `La fecha seleccionada venció hace ${Math.abs(
          diasRestantes
        )} día${Math.abs(diasRestantes) === 1 ? "" : "s"
          }.`,
      };
    }

    if (diasRestantes === 0) {
      return {
        tipo: "critico",
        mensaje: "Los bonos vencen hoy.",
      };
    }

    if (diasRestantes <= 7) {
      return {
        tipo: "critico",
        mensaje: `Los bonos vencerán en ${diasRestantes} día${diasRestantes === 1 ? "" : "s"
          }.`,
      };
    }

    if (diasRestantes <= 30) {
      return {
        tipo: "advertencia",
        mensaje: `Faltan ${diasRestantes} días para el vencimiento.`,
      };
    }

    return {
      tipo: "vigente",
      mensaje: `Faltan ${diasRestantes} días para el vencimiento.`,
    };
  };

  const estadoVencimiento = calcularEstadoVencimiento(
    form.fechaVencimiento
  );

  const cerrarModalBonos = () => {
    if (loading) {
      return;
    }

    setShowUpload(false);
  };

  return (

    <>

    <PageTitle title="Boletería" />

    <div className="inv-root">

      {/* ================= HEADER ================= */}

      <header className="inv-header">

        <h2 className="inv-title">
          | Boletería
        </h2>

        <div className="inv-actions">

          <button
            type="button"
            className="bon-btn-primary"
            onClick={() => setShowUpload(true)}
          >
            <FiFilm size={15} />
            Cargar bonos
          </button>

          <button
            className="bon-btn-primary"
            onClick={() => {
              setSoloProximos(!soloProximos);
              setPage(1);
            }}
          >
            <FiClock size={15} />

            {soloProximos
              ? "Ver todos"
              : "Próximos a vencer"}
          </button>

        </div>

      </header>

      {/* ================= DASHBOARD ================= */}

      <div className="bonos-dashboard">

        {/* ================================= */}
        {/* INVENTARIO POR PRODUCTO */}
        {/* ================================= */}

        <div className="bonos-card">

          <h3>
            Inventario actual por producto
          </h3>

          <div className="productos-stock-list">

            {inventarioPorProducto.map((p) => (

              <div
                key={p.nombre}
                className="productos-stock-row"
              >

                <span className="producto-stock-nombre">
                  {p.nombre}
                </span>

                <strong
                  className={`producto-stock-cantidad ${p.cantidad < 50
                    ? "bajo"
                    : ""
                    }`}
                >
                  {p.cantidad}
                </strong>

              </div>

            ))}

          </div>

        </div>

        {/* ================================= */}
        {/* DISPONIBLES VS PRÓXIMOS A VENCER */}
        {/* ================================= */}

        <div className="bonos-card">

          <h3>
            Disponibles vs Próximos a vencer
          </h3>

          <div className="grafica-wrapper">

            <ResponsiveContainer
              width="100%"
              height="100%"
            >

              <PieChart>

                <Pie
                  data={disponiblesVsVencer}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={85}
                  label
                >

                  <Cell fill="#333" />
                  <Cell fill="rgb(114, 114, 114)" />

                </Pie>

                <Tooltip />

              </PieChart>

            </ResponsiveContainer>

          </div>

        </div>

        {/* ================================= */}
        {/* RESUMEN */}
        {/* ================================= */}

        <div className="bonos-card">

          <h3>
            Resumen
          </h3>

          <div className="bonos-resumen-list">

            <div className="bonos-resumen-row">
              <span>Disponibles</span>
              <span className="bonos-dots" />
              <strong>{resumenBonos.disponibles.toLocaleString("es-CO")}</strong>
            </div>

            <div className="bonos-resumen-row">
              <span>Reservados</span>
              <span className="bonos-dots" />
              <strong>{resumenBonos.reservados.toLocaleString("es-CO")}</strong>
            </div>

            <div className="bonos-resumen-row">
              <span>Entregados</span>
              <span className="bonos-dots" />
              <strong>{resumenBonos.entregados.toLocaleString("es-CO")}</strong>
            </div>

            <div className="bonos-resumen-row">
              <span>Próximos a vencer</span>
              <span className="bonos-dots" />
              <strong>{resumenBonos.proximos.toLocaleString("es-CO")}</strong>
            </div>

          </div>

          <hr />

          <h4
            style={{
              marginBottom: "10px"
            }}
          >
            Próximos a vencer
          </h4>

          <div className="productos-stock-list">

            {proximosPorProducto.length === 0 ? (

              <small>
                No hay bonos próximos a vencer
              </small>

            ) : (

              proximosPorProducto.map((p) => (

                <div
                  key={p.nombre}
                  className="productos-stock-row"
                >

                  <span className="producto-stock-nombre">
                    {p.nombre}
                  </span>

                  <strong>
                    {p.cantidad}
                  </strong>

                </div>

              ))

            )}

          </div>

        </div>

      </div>

      {/* ================= FILTROS ================= */}

      <div className="inv-filters">

        <div className="inv-row">

          {/* SEARCH */}

          <div className="afil-field bonos-search">

            <FiSearch />

            <input
              placeholder="Búsqueda general..."
              value={filtro.search}
              onChange={(e) => {

                setFiltro((prev) => ({
                  ...prev,
                  search: e.target.value,
                }));

                setPage(1);
              }}
            />

          </div>

          {/* CODIGO */}

          <select
            className="inv-select"
            value={filtro.codigo}
            onChange={(e) => {

              setFiltro((prev) => ({
                ...prev,
                codigo: e.target.value,
              }));

              setPage(1);
            }}
          >

            <option value="">
              Todos los códigos
            </option>

            {codigosOptions.map((c) => (

              <option
                key={c}
                value={c}
              >
                {c}
              </option>

            ))}

          </select>

          {/* PROVEEDOR */}

          <select
            className="inv-select"
            value={filtro.proveedor}
            onChange={(e) => {

              setFiltro((prev) => ({
                ...prev,
                proveedor: e.target.value,
              }));

              setPage(1);
            }}
          >

            <option value="">
              Todos los proveedores
            </option>

            {proveedoresOptions.map((p) => (

              <option
                key={p}
                value={p}
              >
                {p}
              </option>

            ))}

          </select>

          {/* PRODUCTO */}

          <select
            className="inv-select"
            value={filtro.producto}
            onChange={(e) => {

              setFiltro((prev) => ({
                ...prev,
                producto: e.target.value,
              }));

              setPage(1);
            }}
          >

            <option value="">
              Todos los productos
            </option>

            {productosOptions.map((p) => (

              <option
                key={p}
                value={p}
              >
                {p}
              </option>

            ))}

          </select>

          {/* LOTE */}

          <select
            className="inv-select"
            value={filtro.lote}
            onChange={(e) => {

              setFiltro((prev) => ({
                ...prev,
                lote: e.target.value,
              }));

              setPage(1);
            }}
          >

            <option value="">
              Todos los lotes
            </option>

            {lotesOptions.map((l) => (

              <option
                key={l}
                value={l}
              >
                {l}
              </option>

            ))}

          </select>

          {/* VIGENCIA */}

          <select
            className="inv-select"
            value={filtro.vigencia}
            onChange={(e) => {

              setFiltro((prev) => ({
                ...prev,
                vigencia: e.target.value,
              }));

              setPage(1);
            }}
          >

            <option value="">
              Toda vigencia
            </option>

            <option value="vencido">
              Vencidos
            </option>

            <option value="30">
              0 - 30 días
            </option>

            <option value="60">
              31 - 60 días
            </option>

            <option value="90">
              61 - 90 días
            </option>

            <option value="90plus">
              +90 días
            </option>

          </select>

          {/* ESTADO */}

          <select
            className="inv-select"
            value={filtro.estado}
            onChange={(e) => {

              setFiltro((prev) => ({
                ...prev,
                estado: e.target.value,
              }));

              setPage(1);
            }}
          >

            <option value="">
              Todos los estados
            </option>

            <option value="disponible">
              Disponible
            </option>

            <option value="reservado">
              Reservado
            </option>

            <option value="entregado">
              Entregado
            </option>

          </select>

          {/* RESET */}

          <button
            className="inv-btn-ghost"
            onClick={() => {

              setFiltro({
                codigo: "",
                proveedor: "",
                producto: "",
                estado: "",
                lote: "",
                vigencia: "",
                search: "",
              });

              setSoloProximos(false);

              setPage(1);
            }}
          >

            Restablecer

          </button>

        </div>

      </div>

      {/* ================= TABLA ================= */}

      <div className="inv-table">

        <table>

          <thead>

            <tr>

              <th>SKU</th>
              <th>Proveedor</th>
              <th>Producto</th>
              <th>PDF</th>
              <th>Lote</th>
              <th>Carga</th>
              <th>Vencimiento</th>
              <th>Vigencia</th>
              <th>Orden</th>
              <th>Cliente</th>
              <th>Entrega</th>
              <th>Estado</th>
              <th>Acciones</th>

            </tr>

          </thead>

          <tbody>

            {paginatedAssets.map((a) => (

              <tr key={a._id}>

                <td>
                  {a.codigo}
                </td>

                <td>
                  {
                    a.proveedor ||
                    a.inventoryItem?.proveedor ||
                    a.inventoryItem?.provider ||
                    "-"
                  }
                </td>

                <td>
                  {a.inventoryItem?.name || "-"}
                </td>

                <td>
                  {toTitleCase(a.archivoNombre)}
                </td>

                <td>
                  {a.lote || "-"}
                </td>

                <td>
                  {
                    a.createdAt
                      ? new Date(a.createdAt)
                        .toLocaleDateString("es-CO")
                      : "-"
                  }
                </td>

                <td>

                  {a.fechaVencimiento
                    ? new Date(a.fechaVencimiento)
                      .toLocaleDateString("es-CO")
                    : "-"}

                </td>

                <td>

                  {a.fechaVencimiento
                    ? (() => {

                      const hoy = new Date();

                      const vencimiento =
                        new Date(a.fechaVencimiento);

                      const diff =
                        Math.ceil(
                          (vencimiento - hoy) /
                          (1000 * 60 * 60 * 24)
                        );

                      if (diff < 0) {
                        return "Vencido";
                      }

                      if (diff <= 60) {
                        return `⚠ ${diff} días`;
                      }

                      return `${diff} días`;
                    })()
                    : "-"}

                </td>

                <td>
                  {
                    a.wooOrderNumber ||
                    a.wooOrderId ||
                    a.despachoId?.codigo ||
                    "-"
                  }
                </td>

                <td>
                  {toTitleCase(a.cliente) || "-"}
                </td>

                <td>

                  {a.fechaEntrega
                    ? new Date(a.fechaEntrega)
                      .toLocaleDateString("es-CO")
                    : "-"}

                </td>

                <td>

                  <span
                    className={`inv-chip ${a.bloqueado
                      ? "inv-chip-bloqueado"
                      : `inv-chip-${a.estado}`
                      }`}
                  >
                    {
                      a.bloqueado
                        ? "Bloqueado"
                        : (
                          a.estado.charAt(0).toUpperCase() +
                          a.estado.slice(1)
                        )
                    }
                  </span>

                </td>

                <td className="inv-actions-row">

                  {/* VER BONO */}

                  <button
                    className="inv-icon-btn"
                    title="Ver bono"
                    onClick={() => {
                      window.open(
                        `${API}/api/digital-assets/${a._id}/pdf`,
                        "_blank"
                      );
                    }}
                  >
                    <FiEye />
                  </button>

                  {/* ENTREGAR */}

                  <button
                    type="button"
                    className="inv-icon-btn success"
                    title="Marcar entregado"
                    onClick={() => {
                      setSelectedAsset(a);

                      setCodigoEntrega("");
                      setClienteEntrega("");
                      setReferenciaEntrega("");

                      setShowEntregaModal(true);
                    }}
                  >
                    <FiCheckCircle />
                  </button>

                  <button
                    className="inv-icon-btn"
                    title={
                      a.bloqueado
                        ? "Desbloquear bono"
                        : "Bloquear bono"
                    }
                    onClick={async () => {

                      try {

                        await axios.patch(
                          `${API}/api/digital-assets/${a._id}/bloquear`
                        );

                        toast.success(
                          a.bloqueado
                            ? "Bono desbloqueado"
                            : "Bono bloqueado"
                        );

                        cargar();

                      } catch (err) {

                        console.error(err);

                        toast.error(
                          "Error actualizando bono"
                        );
                      }
                    }}
                  >
                    {a.bloqueado
                      ? <FiUnlock />
                      : <FiLock />}
                  </button>

                  <button
                    className="inv-icon-btn danger"
                    title="Eliminar bono"
                    onClick={() => {

                      setAssetToDelete(a);

                      setShowDeleteModal(true);
                    }}
                  >
                    <FiTrash2 />
                  </button>

                </td>
              </tr>

            ))}

            {paginatedAssets.length === 0 && (

              <tr>

                <td
                  colSpan="10"
                  style={{
                    textAlign: "center",
                  }}
                >
                  Sin resultados
                </td>

              </tr>

            )}

          </tbody>

        </table>

        {/* ================= PAGINACIÓN ================= */}

        <div className="inv-paginacion">

          <button
            disabled={page === 1}
            onClick={() =>
              setPage(page - 1)
            }
          >
            Anterior
          </button>

          <span className="inv-pagina-info">

            Página {page} de {pages || 1}

          </span>

          <button
            disabled={
              page === pages ||
              pages === 0
            }
            onClick={() =>
              setPage(page + 1)
            }
          >
            Siguiente
          </button>

        </div>


      </div>

      {/* =========================================
          MODAL ENTREGA
      ========================================= */}

      {showEntregaModal && (

        <div className="bonos-modal-backdrop">

          <div className="bonos-modal">

            <h3>
              Confirmar entrega
            </h3>

            <p>
              Ingresa el código de autorización
            </p>

            <div className="bonos-modal-fields">

              <input
                type="password"
                autoComplete="new-password"
                name="codigoEntregaAutorizacion"
                value={codigoEntrega}
                onChange={(e) => setCodigoEntrega(e.target.value)}
                placeholder="Código"
              />

              <input
                type="text"
                autoComplete="off"
                name="clienteEntrega"
                value={clienteEntrega}
                onChange={(e) => setClienteEntrega(e.target.value)}
                placeholder="Nombre del cliente"
              />

              <input
                type="text"
                autoComplete="off"
                name="referenciaEntrega"
                value={referenciaEntrega}
                onChange={(e) => setReferenciaEntrega(e.target.value)}
                placeholder="Pedido / referencia"
              />

            </div>

            <div className="bonos-modal-actions">

              <button
                className="inv-btn-ghost"
                onClick={() => {

                  setShowEntregaModal(false);

                  setCodigoEntrega("");

                  setSelectedAsset(null);

                  setClienteEntrega("");
                }}
              >
                Cancelar
              </button>

              <button
                className="inv-btn-primary"
                onClick={async () => {

                  if (codigoEntrega !== "830077032") {

                    toast.error(
                      "Código incorrecto"
                    );

                    return;
                  }

                  if (!clienteEntrega.trim()) {

                    toast.error(
                      "Ingresa el nombre del cliente"
                    );

                    return;
                  }

                  try {

                    await axios.patch(
                      `${API}/api/digital-assets/${selectedAsset._id}/entregar`,
                      {
                        cliente: clienteEntrega,
                        referencia: referenciaEntrega,
                      }
                    );

                    toast.success(
                      "Bono marcado como entregado"
                    );

                    setShowEntregaModal(false);

                    setCodigoEntrega("");

                    setSelectedAsset(null);

                    setClienteEntrega("");

                    cargar();

                  } catch (err) {

                    console.error(err);

                    toast.error(
                      err.response?.data?.message ||
                      "Error entregando bono"
                    );
                  }
                }}
              >
                Confirmar
              </button>

            </div>

          </div>

        </div>

      )}

      {showDeleteModal && (

        <div className="bonos-modal-backdrop">

          <div className="bonos-modal">

            <h3>
              Eliminar bono
            </h3>

            <p>
              ¿Eliminar bono definitivamente?
            </p>

            <div className="bonos-modal-actions">

              <button
                className="inv-btn-ghost"
                onClick={() => {

                  setShowDeleteModal(false);

                  setAssetToDelete(null);
                }}
              >
                Cancelar
              </button>

              <button
                className="inv-btn-danger"
                onClick={async () => {

                  try {

                    const token = localStorage.getItem("token");

                    await axios.delete(
                      `${API}/api/digital-assets/${assetToDelete._id}`,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );

                    toast.success(
                      "Bono eliminado"
                    );

                    setShowDeleteModal(false);

                    setAssetToDelete(null);

                    cargar();

                  } catch (err) {

                    console.error(err);

                    toast.error(
                      "Error eliminando bono"
                    );
                  }
                }}
              >
                Eliminar
              </button>

            </div>

          </div>

        </div>
      )}

      {showUpload &&
        createPortal(
          <div
            className="bonos-modal-overlay"
            onClick={cerrarModalBonos}
          >
            <div
              className="bonos-upload-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bonos-upload-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bonos-modal-header">
                <div>
                  <h3 id="bonos-upload-title">
                    Cargar Bonos Digitales
                  </h3>

                  <p>
                    Sube los archivos PDF asociados al inventario
                    y confirma su fecha de vencimiento.
                  </p>
                </div>

                <button
                  type="button"
                  className="bonos-modal-close"
                  onClick={cerrarModalBonos}
                  disabled={loading}
                  aria-label="Cerrar modal"
                >
                  ×
                </button>
              </div>

              <form
                className="bonos-upload-form"
                onSubmit={handleSubmit}
              >
                <div className="bonos-form-group">
                  <label htmlFor="inventoryItem">
                    Producto del inventario
                  </label>

                  <select
                    id="inventoryItem"
                    name="inventoryItem"
                    value={form.inventoryItem}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      Seleccionar producto
                    </option>

                    {productosDigitales.map((item) => (
                      <option
                        key={item._id}
                        value={item._id}
                      >
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bonos-form-group">
                  <label htmlFor="lote">
                    Lote de carga
                  </label>

                  <input
                    id="lote"
                    type="text"
                    name="lote"
                    value={form.lote}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="bonos-form-group">
                  <label htmlFor="fechaVencimiento">
                    Fecha de vencimiento
                  </label>

                  <input
                    id="fechaVencimiento"
                    type="date"
                    name="fechaVencimiento"
                    value={form.fechaVencimiento}
                    onChange={handleChange}
                    required
                  />

                  {estadoVencimiento && (
                    <div
                      className={`bonos-vencimiento-alert bonos-vencimiento-${estadoVencimiento.tipo}`}
                      role="alert"
                    >
                      <strong>
                        Estado del vencimiento
                      </strong>

                      <span>
                        {estadoVencimiento.mensaje}
                      </span>
                    </div>
                  )}
                </div>

                <div className="bonos-form-group">
                  <label htmlFor="archivosBonos">
                    Archivos PDF
                  </label>

                  <input
                    id="archivosBonos"
                    key={resetFileKey}
                    type="file"
                    accept=".pdf,application/pdf"
                    multiple
                    onChange={(e) =>
                      setArchivos(
                        Array.from(e.target.files || [])
                      )
                    }
                    required
                  />

                  {archivos.length > 0 && (
                    <div className="bonos-files-summary">
                      {archivos.length} archivo
                      {archivos.length === 1 ? "" : "s"} seleccionado
                      {archivos.length === 1 ? "" : "s"}.
                    </div>
                  )}
                </div>

                <div className="bonos-modal-actions-II">
                  <button
                    type="submit"
                    className="bon-btn-primary"
                    disabled={
                      loading ||
                      !form.inventoryItem ||
                      !form.lote ||
                      !form.fechaVencimiento ||
                      archivos.length === 0 ||
                      estadoVencimiento?.tipo === "vencido"
                    }
                  >
                    {loading
                      ? "Subiendo..."
                      : `Subir ${archivos.length > 0
                        ? archivos.length
                        : ""
                      } bono${archivos.length === 1 ? "" : "s"
                      }`}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

    </div>

    </>
    
  );
}

export default BancoBonos;